import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = not authed
  const [loading, setLoading] = useState(true);
  // Guards against a race where an in-flight /auth/me call (fired on initial mount,
  // before login/register happened) resolves AFTER a subsequent login/register/logout
  // action and overwrites the fresher user state with a stale result.
  const authGenerationRef = useRef(0);

  const checkAuth = useCallback(async () => {
    const myGeneration = ++authGenerationRef.current;
    try {
      const { data } = await api.get("/auth/me");
      if (authGenerationRef.current === myGeneration) setUser(data);
    } catch (e) {
      if (authGenerationRef.current === myGeneration) setUser(false);
    } finally {
      if (authGenerationRef.current === myGeneration) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      authGenerationRef.current += 1;
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, referralCode) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, referral_code: referralCode || undefined });
      authGenerationRef.current += 1;
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const registerRecruiter = async (email, password, name, companyName) => {
    try {
      const { data } = await api.post("/auth/register-recruiter", { email, password, name, company_name: companyName });
      authGenerationRef.current += 1;
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    authGenerationRef.current += 1;
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout API call failed, clearing local session anyway:", e);
    }
    setUser(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, registerRecruiter, logout, loginWithGoogle, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
